import { z } from 'zod';
import { DATE_FORMATS, LANGUAGES, type DateFormat, type Language } from '../constants/locale';
import { CURRENCIES, type Currency } from '../money/money';
import { NOTIFICATION_SMS_FALLBACK_MINUTES_RANGE } from './driver-notification.schemas';
import { NEGATIVE_BALANCE_TREATMENTS, type NegativeBalanceTreatment } from './management-agreement.schemas';

/** A point on the map: the company's own location, say. */
export const lngLatSchema = z.object({
  lng: z.number().min(-180).max(180),
  lat: z.number().min(-90).max(90),
});
export type LngLat = z.infer<typeof lngLatSchema>;

/** Bounds of the repeat-repair window; a CHECK on `organization` holds the same. */
export const MIN_REPEAT_REPAIR_WINDOW_MONTHS = 1;
export const MAX_REPEAT_REPAIR_WINDOW_MONTHS = 60;

/** Bounds of the rolling fuel economy settings (gitaumoses4/chukua#110); CHECKs on `organization` hold the same. */
export const FUEL_WINDOW_TRIPS_RANGE = { min: 3, max: 50 } as const;
export const FUEL_MIN_SAMPLES_RANGE = { min: 1, max: 20 } as const;
export const FUEL_OUTLIER_PERCENT_RANGE = { min: 10, max: 100 } as const;

/** Bounds of the trip plan's rest allowance (gitaumoses4/chukua#410); CHECKs on `organization` hold the same. */
export const TRIP_DRIVING_HOURS_PER_DAY_RANGE = { min: 1, max: 24 } as const;
export const TRIP_REST_HOURS_PER_DAY_RANGE = { min: 0, max: 48 } as const;

const weight = z.number().int().min(0).max(100).optional();

export const updateOrganizationSchema = z.object({
  name: z.string().trim().min(1).max(80),
  legalName: z.string().trim().min(1).max(160).optional(),
  kraPin: z.string().trim().min(1).max(20).optional(),
  address: z.string().trim().min(1).max(500).optional(),
  /** Where the company is (its head office or main yard): shown on the Zones map. Omit to keep it; null clears it. */
  location: lngLatSchema.nullable().optional(),
  /** The country the company operates from (ISO 3166-1 alpha-2): a trip into a zone of another country is cross-border (gitaumoses4/chukua#47). Omit to keep it. */
  countryCode: z
    .string()
    .trim()
    .regex(/^[A-Za-z]{2}$/, 'Use a two-letter country code, such as KE')
    .transform((code) => code.toUpperCase())
    .optional(),
  currency: z.enum(CURRENCIES).optional(),
  timezone: z.string().trim().min(1).max(80).optional(),
  dateFormat: z.enum(DATE_FORMATS).optional(),
  language: z.enum(LANGUAGES).optional(),
  /** How many days out a job's deadline counts as "due soon" (gitaumoses4/chukua#39) — read by the jobs list, not shown on the public profile. */
  jobDueSoonThresholdDays: z.number().int().min(1).max(365).optional(),
  /** How many days overdue a customer's invoices must be before the credit check at job confirmation treats them as "badly overdue" (gitaumoses4/chukua#37) — read by the credit-check seam, not shown on the public profile. */
  creditOverdueThresholdDays: z.number().int().min(1).max(365).optional(),
  /** Hours a driver rests after a trip ends before they count as available again (gitaumoses4/chukua#54). 0 means no rest period. */
  driverRestHours: z.number().int().min(0).max(72).optional(),
  /** Kilometres before a service is due at which dispatch eligibility starts to warn (gitaumoses4/chukua#49). Overdue always blocks. */
  serviceDueSoonKm: z.number().int().min(0).max(100_000).optional(),
  /** Days before a service is due at which dispatch eligibility starts to warn (gitaumoses4/chukua#49). */
  serviceDueSoonDays: z.number().int().min(0).max(365).optional(),
  /** Share of the limiting capacity, in whole percent, at which a load warns (gitaumoses4/chukua#49). Over capacity always blocks. */
  capacityWarningPercent: z.number().int().min(50).max(100).optional(),
  /** Calendar months within which the same part type fitted again on a truck flags the work order as a repeat repair (gitaumoses4/chukua#175). */
  repeatRepairWindowMonths: z.number().int().min(MIN_REPEAT_REPAIR_WINDOW_MONTHS).max(MAX_REPEAT_REPAIR_WINDOW_MONTHS).optional(),
  /** How many of a truck's latest trips its rolling fuel economy averages over (gitaumoses4/chukua#110). */
  fuelWindowTrips: z.number().int().min(FUEL_WINDOW_TRIPS_RANGE.min).max(FUEL_WINDOW_TRIPS_RANGE.max).optional(),
  /** Trips with fuel recorded needed in a window before the rolling figure replaces the truck's starting value (gitaumoses4/chukua#110). */
  fuelMinSamples: z.number().int().min(FUEL_MIN_SAMPLES_RANGE.min).max(FUEL_MIN_SAMPLES_RANGE.max).optional(),
  /** A trip burning more than this many percent away from its window's median is left out of the rolling figure (gitaumoses4/chukua#110). */
  fuelOutlierPercent: z.number().int().min(FUEL_OUTLIER_PERCENT_RANGE.min).max(FUEL_OUTLIER_PERCENT_RANGE.max).optional(),
  /** Hours of driving that earn one rest allowance when a trip plan's map time is padded (gitaumoses4/chukua#410). Not the driver's rest after close-out (`driverRestHours`). */
  tripDrivingHoursPerDay: z.number().int().min(TRIP_DRIVING_HOURS_PER_DAY_RANGE.min).max(TRIP_DRIVING_HOURS_PER_DAY_RANGE.max).optional(),
  /** Hours added to a trip plan's time for every `tripDrivingHoursPerDay` hours of driving (gitaumoses4/chukua#410). */
  tripRestHoursPerDay: z.number().int().min(TRIP_REST_HOURS_PER_DAY_RANGE.min).max(TRIP_REST_HOURS_PER_DAY_RANGE.max).optional(),
  /** Minutes a driver's phone may go without calling the server before their notifications go by SMS instead of push (gitaumoses4/chukua#61). */
  notificationSmsFallbackMinutes: z.number().int().min(NOTIFICATION_SMS_FALLBACK_MINUTES_RANGE.min).max(NOTIFICATION_SMS_FALLBACK_MINUTES_RANGE.max).optional(),
  /** How much each factor counts when dispatch ranks suggested crews, 0–100 (gitaumoses4/chukua#52). A factor left out keeps its weight; they cannot all be zero. */
  suggestionWeights: z
    .object({
      proximity: weight,
      freeAt: weight,
      emptyKm: weight,
      serviceDistance: weight,
      familiarity: weight,
      coupling: weight,
    })
    .optional(),
  /** How many suggested crews the picker shows per page (gitaumoses4/chukua#52). */
  suggestionPageSize: z.number().int().min(1).max(100).optional(),
  /** Management fee a new agreement starts with, in basis points (gitaumoses4/chukua#182). Copied onto the agreement when it is created, so changing it never moves an existing one. */
  defaultFeeRateBps: z.number().int().min(0).max(10_000).optional(),
  /** How a new agreement treats a negative balance unless told otherwise (gitaumoses4/chukua#182); copied onto the agreement like the fee rate. */
  defaultNegativeBalanceTreatment: z.enum(NEGATIVE_BALANCE_TREATMENTS).optional(),
  /** Whether dispatch refuses a driver who still owes cash from a finished trip (gitaumoses4/chukua#127). An approver can override it either way. */
  dispatchBlocksUnreconciledCash: z.boolean().optional(),
});
export type UpdateOrganizationInput = z.infer<typeof updateOrganizationSchema>;

/** Default for a deployment that has never set `jobDueSoonThresholdDays` — the value the migration backfills too. */
export const DEFAULT_JOB_DUE_SOON_THRESHOLD_DAYS = 30;

/** Default for a deployment that has never set `creditOverdueThresholdDays` — the value the migration backfills too. Placeholder, not sourced from the PRD's ageing buckets — see gitaumoses4/chukua#37. */
export const DEFAULT_CREDIT_OVERDUE_THRESHOLD_DAYS = 60;

/** Default for a deployment that has never set `driverRestHours` — the value the migration backfills too. */
export const DEFAULT_DRIVER_REST_HOURS = 8;

/** Defaults for a deployment that has never set the dispatch eligibility warning windows — the values the migration backfills too (gitaumoses4/chukua#49). */
export const DEFAULT_REPEAT_REPAIR_WINDOW_MONTHS = 6;
export const DEFAULT_SERVICE_DUE_SOON_KM = 1000;
export const DEFAULT_SERVICE_DUE_SOON_DAYS = 14;
export const DEFAULT_CAPACITY_WARNING_PERCENT = 95;

/** Default for a deployment that has never set `dispatchBlocksUnreconciledCash` — the value the migration gives existing rows too. */
export const DEFAULT_DISPATCH_BLOCKS_UNRECONCILED_CASH = true;

/** Defaults for the rolling fuel economy (gitaumoses4/chukua#110) — the values the migration backfills too. Placeholders for Index Cargo to confirm. */
export const DEFAULT_FUEL_WINDOW_TRIPS = 10;
export const DEFAULT_FUEL_MIN_SAMPLES = 3;
export const DEFAULT_FUEL_OUTLIER_PERCENT = 40;

/** Defaults for the trip plan's rest allowance (gitaumoses4/chukua#410) — the values the migration backfills too. 8 h of driving pads to 19 h, the Mombasa → Nairobi truck median; placeholders for Index Cargo to confirm. */
export const DEFAULT_TRIP_DRIVING_HOURS_PER_DAY = 8;
export const DEFAULT_TRIP_REST_HOURS_PER_DAY = 11;

/** How a trip plan's driving time is padded before it is judged against a deadline; set on the company page. */
export interface TripBufferSettings {
  tripDrivingHoursPerDay: number;
  tripRestHoursPerDay: number;
}

/** How a truck's rolling fuel economy is worked out; set on the company page. */
export interface FuelEconomySettings {
  fuelWindowTrips: number;
  fuelMinSamples: number;
  fuelOutlierPercent: number;
}

/** How close to a limit counts as "warn" rather than "pass" in the dispatch eligibility check; set on the company page. */
export interface EligibilityWarningWindows {
  serviceDueSoonKm: number;
  serviceDueSoonDays: number;
  capacityWarningPercent: number;
}

/** Defaults for a deployment that has never set the suggestion weights — the values the migration backfills too (gitaumoses4/chukua#52). A starting point to tune, not a business rule. */
export const DEFAULT_SUGGESTION_WEIGHTS: SuggestionWeights = {
  proximity: 30,
  freeAt: 25,
  emptyKm: 20,
  serviceDistance: 15,
  familiarity: 10,
  coupling: 0,
};
export const DEFAULT_SUGGESTION_PAGE_SIZE = 10;

/** How much each factor counts when dispatch ranks suggested crews; only the proportions matter. */
export interface SuggestionWeights {
  proximity: number;
  freeAt: number;
  emptyKm: number;
  serviceDistance: number;
  familiarity: number;
  coupling: number;
}

/** What the web app shows for the company this deployment serves. Public: the login page needs it. */
export interface OrganizationProfile {
  /** False until the deployment has been given its company; the UI then shows Chukua's own branding. */
  configured: boolean;
  name: string;
  legalName: string | null;
  /** Same-origin, versioned (safe to cache forever). Null when no logo has been set. */
  logoUrl: string | null;
  /** Variant for dark surfaces; null means use `logoUrl` there too. */
  logoDarkUrl: string | null;
  /** ISO 3166-1 alpha-2 of the country the company works from; null until set. Public: the login page draws that country's outline under its rig. */
  countryCode: string | null;
  currency: Currency;
  timezone: string;
  dateFormat: DateFormat;
  language: Language;
}

/** The admin-only settings screen: the public profile plus the company's tax registration and location. */
export interface OrganizationSettings extends OrganizationProfile, FuelEconomySettings, TripBufferSettings {
  kraPin: string | null;
  address: string | null;
  /** Never on the public profile: the login page has no need of where the company is. */
  location: LngLat | null;
  jobDueSoonThresholdDays: number;
  creditOverdueThresholdDays: number;
  driverRestHours: number;
  serviceDueSoonKm: number;
  serviceDueSoonDays: number;
  capacityWarningPercent: number;
  repeatRepairWindowMonths: number;
  notificationSmsFallbackMinutes: number;
  suggestionWeights: SuggestionWeights;
  suggestionPageSize: number;
  defaultFeeRateBps: number;
  defaultNegativeBalanceTreatment: NegativeBalanceTreatment;
  dispatchBlocksUnreconciledCash: boolean;
}

/** Before a deployment is given its company, both apps show Chukua's own branding and these defaults. */
export const UNCONFIGURED_ORGANIZATION: OrganizationProfile = {
  configured: false,
  name: 'Chukua',
  legalName: null,
  logoUrl: null,
  logoDarkUrl: null,
  countryCode: null,
  currency: 'KES',
  timezone: 'Africa/Nairobi',
  dateFormat: 'DD/MM/YYYY',
  language: 'en',
};
